/**
 * BillingToggle Component
 *
 * Toggle between monthly and annual billing with savings indicator.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withSpring,
  useSharedValue,
  interpolateColor,
} from 'react-native-reanimated';
import { Percent } from 'lucide-react-native';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import type { BillingInterval } from '@/lib/subscription/types';
import { ANNUAL_DISCOUNT_PERCENT } from '@/lib/subscription/pricing';

// ============================================================================
// Types
// ============================================================================

interface BillingToggleProps {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  disabled?: boolean;
}

// ============================================================================
// Main Component
// ============================================================================

export function BillingToggle({ value, onChange, disabled = false }: BillingToggleProps) {
  const colors = useThemeColors();
  const isAnnual = value === 'year';

  const handleToggle = (interval: BillingInterval) => {
    if (!disabled && interval !== value) {
      onChange(interval);
    }
  };

  return (
    <View style={[styles.container, !isWeb && styles.containerMobile]}>
      {/* Toggle Container */}
      <View
        style={[
          styles.toggleContainer,
          { backgroundColor: colors.surface.secondary },
        ]}
      >
        {/* Monthly Option */}
        <AnimatedPressable
          onPress={() => handleToggle('month')}
          disabled={disabled}
          style={[
            styles.option,
            !isAnnual && [
              styles.activeOption,
              { backgroundColor: colors.primary[600] },
            ],
          ]}
        >
          <Text
            style={[
              styles.optionText,
              {
                color: !isAnnual ? '#fff' : colors.text.tertiary,
                fontWeight: !isAnnual ? '700' : '500',
              },
            ]}
          >
            Monthly
          </Text>
        </AnimatedPressable>

        {/* Annual Option */}
        <AnimatedPressable
          onPress={() => handleToggle('year')}
          disabled={disabled}
          style={[
            styles.option,
            isAnnual && [
              styles.activeOption,
              { backgroundColor: colors.primary[600] },
            ],
          ]}
        >
          <Text
            style={[
              styles.optionText,
              {
                color: isAnnual ? '#fff' : colors.text.tertiary,
                fontWeight: isAnnual ? '700' : '500',
              },
            ]}
          >
            Annual
          </Text>
        </AnimatedPressable>
      </View>

      {/* Savings Badge */}
      <View
        style={[
          styles.savingsBadge,
          {
            backgroundColor: isAnnual
              ? colors.traffic.green
              : `${colors.traffic.green}20`,
          },
        ]}
      >
        <Percent
          size={12}
          color={isAnnual ? '#fff' : colors.traffic.green}
          strokeWidth={2.5}
        />
        <Text
          style={[
            styles.savingsText,
            { color: isAnnual ? '#fff' : colors.traffic.green },
          ]}
        >
          Save {ANNUAL_DISCOUNT_PERCENT}%
        </Text>
      </View>
    </View>
  );
}

// ============================================================================
// Compact Variant
// ============================================================================

interface BillingToggleCompactProps {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
}

export function BillingToggleCompact({ value, onChange }: BillingToggleCompactProps) {
  const colors = useThemeColors();
  const isAnnual = value === 'year';

  return (
    <AnimatedPressable
      onPress={() => onChange(isAnnual ? 'month' : 'year')}
      style={[
        styles.compactContainer,
        { backgroundColor: colors.surface.secondary },
      ]}
    >
      <View
        style={[
          styles.compactToggle,
          {
            backgroundColor: colors.primary[600],
            transform: [{ translateX: isAnnual ? 28 : 0 }],
          },
        ]}
      />
      <Text
        style={[
          styles.compactLabel,
          styles.compactLabelLeft,
          { color: !isAnnual ? '#fff' : colors.text.tertiary },
        ]}
      >
        Mo
      </Text>
      <Text
        style={[
          styles.compactLabel,
          styles.compactLabelRight,
          { color: isAnnual ? '#fff' : colors.text.tertiary },
        ]}
      >
        Yr
      </Text>
    </AnimatedPressable>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    alignSelf: 'center',
  },
  containerMobile: {
    flexDirection: 'column',
    gap: 8,
  },
  toggleContainer: {
    flexDirection: 'row',
    borderRadius: 12,
    padding: 4,
  },
  option: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 10,
    minWidth: 84,
    alignItems: 'center',
  },
  activeOption: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  optionText: {
    fontSize: 14,
  },
  savingsBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
  },
  savingsText: {
    fontSize: 12,
    fontWeight: '600',
  },
  // Compact variant styles
  compactContainer: {
    width: 60,
    height: 28,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  compactToggle: {
    position: 'absolute',
    width: 28,
    height: 24,
    borderRadius: 12,
    marginLeft: 2,
  },
  compactLabel: {
    flex: 1,
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    zIndex: 1,
  },
  compactLabelLeft: {
    marginLeft: 2,
  },
  compactLabelRight: {
    marginRight: 2,
  },
});
